/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-11-23 15:00:00
 *
 *******************************************************/
//@flow

import _ from 'lodash';

import React, { Component } from 'react';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { Card, TextStyle, Heading, ProgressBar, Button, SkeletonBodyText } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { showBeaconChat } from 'src/integrations/beacon';
import { connect } from 'react-redux';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';
import * as managementActions from 'actions/filter-tree/management-actions';
import * as dashboardActionV3 from 'actions/dashboard-v3-actions';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import { getFrontStoreLink, getFullAppUrlThemes, buildRedirectUrl, getShop } from 'src/helpers';
import {
  getAllGuideVisibleByFeature,
  getAllStepVisibleByFeature,
  findGuideOpening,
  newKeyFromOldKeyGuide
} from 'src/helpers/quick-start-guide-v2-helper';
import { getCorrectPlan } from 'src/helpers/subscription-v3-helper';
import { GuideV2ItemStep, GuideV2Item, GuideMenu } from 'src/components/views/quick-start-guide';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';
import {
  MIN_PLAN_PRICE,
  MIN_TOTAL_VALUE_OF_PLANS,
  DATA_GUIDE_V2,
  STATUS_INSTALL_ON_THEME
} from 'src/constants/dashboard-layout-v3';
import IconReload from 'src/assets/images/quick-start-guide/icon.reload.svg';
import IconStar from 'src/assets/images/quick-start-guide/icon.star.svg';
import IconChecked from 'src/assets/images/quick-start-guide/icon.checked.svg';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import ImgPreviewSSR from 'src/assets/images/quick-start-guide/img.preview4.ssr.svg';

type Props = {
  defaultFilterId: string,
  steps: Array,
  loading: boolean,
  success: boolean,
  showCollapseIcon?: boolean,
  themes: Array
};

type ComposedProps = Props & WithI18nProps;

type State = {
  indexOpenFilter: number,
  indexOpenFilterAdvanced: number,
  indexOpenSearch: number,
  indexOpenRecommend: number,
  indexOpenPreview: number,
  isExploreFilterAdvanced: boolean
};

const mapFeature = {
  V2_FILTER: 'indexOpenFilter',
  V2_SEARCH: 'indexOpenSearch',
  V2_RECOMMEND: 'indexOpenRecommend',
  V2_PREVIEW: 'indexOpenPreview'
};

const mapTrigger = {
  V2_FILTER: 'isTriggerStepChildFilter',
  V2_SEARCH: 'isTriggerStepChildSearch',
  V2_RECOMMEND: 'isTriggerStepChildRecommend',
  V2_PREVIEW: 'isTriggerStepChildPreview'
};

/**
 * Each block has title, description as text, this function to insert a html for title, descriotion as advanced
 * @param {*} i18n
 * @param {*} statusInstalledApp
 * @param {*} data
 * @param {*} onGotoThemeSetup
 * @returns
 */
export const generateGuidePreviewContent = (i18n, statusInstalledApp, data, onGotoThemeSetup) => {
  const {
    title: titleCase,
    title_t: titleTCase,
    description: descriptionCase,
    description_t: descriptionTCase
  } = data;

  let titleComponent = null;
  let descriptionComponent = null;

  if (statusInstalledApp === 0) {
    titleComponent = (
      <span>
        {i18n.translate(titleCase, {
          t: <span style={{ color: '#F48D32' }}>{i18n.translate(titleTCase)}</span>
        })}
      </span>
    );
    descriptionComponent = (
      <span>
        {i18n.translate(descriptionCase, {
          t: (
            <u onClick={onGotoThemeSetup}>
              <span style={{ color: '#2C6ECB', cursor: 'pointer' }}>
                {i18n.translate(descriptionTCase)}
              </span>
            </u>
          )
        })}
      </span>
    );
  }

  if (statusInstalledApp === 1) {
    titleComponent = (
      <span>
        {i18n.translate(titleCase, {
          t: <span style={{ color: '#D72C0D' }}>{i18n.translate(titleTCase)}</span>
        })}
      </span>
    );
    descriptionComponent = <span>{i18n.translate(descriptionCase)}</span>;
  }

  if (statusInstalledApp === 2) {
    titleComponent = (
      <span>
        {i18n.translate(titleCase, {
          t: <span style={{ color: '#008060' }}>{i18n.translate(titleTCase)}</span>
        })}
      </span>
    );
    descriptionComponent = <span>{i18n.translate(descriptionCase)}</span>;
  }

  return {
    titleComponent,
    descriptionComponent
  };
};

class QuickStartGuideV2 extends Component<ComposedProps, State> {
  state = {
    guideOpen: null,
    featureClickedClose: null,
    indexOpenFilter: null,
    indexOpenSearch: null,
    indexOpenRecommend: null,
    indexOpenPreview: null,
    indexOpenFilterAdvanced: null,
    isExploreFilterAdvanced: false,
    isTriggerStepChildFilter: null,
    isTriggerStepChildSearch: null,
    isTriggerStepChildRecommend: null,
    isTriggerStepChildPreview: null,
    isTriggerStepChildFilterAdvanced: null,
    isCollapseGuide: false,
    isCollapsingGuide: false,
    isHideGuideForever: false,
    isHideForeverGuideFilter: false,
    isHideForeverGuideSearch: false,
    isHideForeverGuideRecommendation: false,
    isClickedGuideAtLeastOneTime: false // this state to detect if guide auto open, or auto open and then clicked
  };

  componentDidMount() {
    const { getQuickStartGuides, listFilterTree, getThemes } = this.props;

    // Get list of notification
    getQuickStartGuides();
    getThemes();
    listFilterTree('', 0);

    try {
      const guideConfigString = localStorage.getItem(`${getShop()}-guide`);
      const featureCloseClicked = localStorage.getItem(`${getShop()}-guide-feature-close-clicked`);

      const guideConfig = guideConfigString
        ? JSON.parse(guideConfigString)
        : {
            isCollapseGuide: false,
            isHideGuideForever: false,
            isHideForeverGuideFilter: false,
            isHideForeverGuideSearch: false,
            isHideForeverGuideRecommendation: false
          };

      if (featureCloseClicked) {
        guideConfig[`isHideForeverGuide${featureCloseClicked}`] = true;
      }

      this.setState(
        {
          isCollapseGuide: guideConfig.isCollapseGuide,
          isHideGuideForever: guideConfig.isHideGuideForever,
          isHideForeverGuideFilter: guideConfig.isHideForeverGuideFilter,
          isHideForeverGuideSearch: guideConfig.isHideForeverGuideSearch,
          isHideForeverGuideRecommendation: guideConfig.isHideForeverGuideRecommendation
        },
        () => {
          this.updateLocalStorageConfig();
          localStorage.removeItem(`${getShop()}-guide-feature-close-clicked`);
        }
      );
    } catch (error) {}
  }

  updateLocalStorageConfig = () => {
    try {
      const {
        isCollapseGuide,
        isHideGuideForever,
        isHideForeverGuideFilter,
        isHideForeverGuideSearch,
        isHideForeverGuideRecommendation
      } = this.state;
      localStorage.setItem(
        `${getShop()}-guide`,
        JSON.stringify({
          isCollapseGuide,
          isHideGuideForever,
          isHideForeverGuideFilter,
          isHideForeverGuideSearch,
          isHideForeverGuideRecommendation
        })
      );
    } catch (error) {}
    Emitter.emit(EVENT_KEY.UPDATE_LOCAL_STORAGE);
  };

  checkStepPassed = stepInDB => {
    return _.uniq(
      stepInDB
        .filter(step => step.isDone)
        .map(step => {
          const { name } = step;
          if (name.includes('V2')) return name;
          return newKeyFromOldKeyGuide(name);
        })
        .filter(name => name)
    );
  };

  onOpen = (keyGuide, index, firstStatusOpen) => {
    if (keyGuide === 'V2_FILTER') {
      this.setState({
        indexOpenFilter: firstStatusOpen || index === this.state.indexOpenFilter ? null : index,
        isTriggerStepChildFilter: true,
        isTriggerStepChildFilterAdvanced: null,
        isTriggerStepChildSearch: null,
        isTriggerStepChildRecommend: null,
        isTriggerStepChildPreview: null
      });
    }
    if (keyGuide === 'V2_SEARCH') {
      this.setState({
        indexOpenSearch: firstStatusOpen || index === this.state.indexOpenSearch ? null : index,
        isTriggerStepChildFilter: null,
        isTriggerStepChildFilterAdvanced: null,
        isTriggerStepChildSearch: true,
        isTriggerStepChildRecommend: null,
        isTriggerStepChildPreview: null
      });
    }
    if (keyGuide === 'V2_RECOMMEND') {
      this.setState({
        indexOpenRecommend:
          firstStatusOpen || index === this.state.indexOpenRecommend ? null : index,
        isTriggerStepChildFilter: null,
        isTriggerStepChildFilterAdvanced: null,
        isTriggerStepChildSearch: null,
        isTriggerStepChildRecommend: true,
        isTriggerStepChildPreview: null
      });
    }
    if (keyGuide === 'V2_PREVIEW') {
      this.setState({
        indexOpenPreview: firstStatusOpen || index === this.state.indexOpenPreview ? null : index,
        isTriggerStepChildFilter: null,
        isTriggerStepChildFilterAdvanced: null,
        isTriggerStepChildSearch: null,
        isTriggerStepChildRecommend: null,
        isTriggerStepChildPreview: true
      });
    }
  };

  onOpenFilterAdvanced = (keyGuide, index, firstStatusOpen) => {
    this.setState({
      indexOpenFilterAdvanced: firstStatusOpen ? null : index,
      isTriggerStepChildFilter: false,
      isTriggerStepChildFilterAdvanced: true,
      isTriggerStepChildSearch: false,
      isTriggerStepChildRecommend: false,
      isTriggerStepChildPreview: false
    });
  };

  checkThemes = (themes = []) => {
    if (!themes || !themes.length) return STATUS_INSTALL_ON_THEME.NOT_INSTALLED_ON_THEME;
    const liveTheme = _.find(themes, theme => theme.published);
    if (liveTheme) {
      return STATUS_INSTALL_ON_THEME.INSTALLED_ON_LIVE_THEME;
    }
    return STATUS_INSTALL_ON_THEME.INSTALLED_ON_NOT_LIVE_THEME;
  };

  isAdaptSubscriptionPlanWhatsApp = props => {
    const { isAdaptMinTotalValue, correctPlanPrice, originalPlan } = props;
    // We will skip this V1 plan because the mapping price to V3 plan is not correct (all V1 plan price are < $900)
    const isOldPlan = originalPlan?.includes('plan');
    return (
      (correctPlanPrice >= MIN_PLAN_PRICE && !!originalPlan && !isOldPlan) || isAdaptMinTotalValue
    );
  };

  startSupport = feature => {
    this.onSetClickAdvancedFeature(feature);
    if (this.isAdaptSubscriptionPlanWhatsApp(this.props)) {
      this.openWhatsApp();
    } else {
      this.openBeaconChat();
    }
  };

  openBeaconChat = () => {
    showBeaconChat();
  };

  openWhatsApp = () => {
    window.open(
      'https://api.whatsapp.com/message/Z3VN5CQZPYIZO1?autoload=1&app_absent=0',
      '_blank'
    );
  };

  onGotoThemeSetup = () => {
    const shop = getShop();
    const redirectUrl = buildRedirectUrl(shop, routes.themeSetup.view());
    window.open(redirectUrl, '_blank');
  };

  onSetClickAdvancedFeature = feature => {
    try {
      localStorage.setItem(`${getShop()}-guide-${feature}-advanced-clicked`, true);
    } catch (error) {}
  };

  onExploreAddon = () => {
    this.onSetClickAdvancedFeature('search');
    const shop = getShop();
    const redirectUrl = buildRedirectUrl(shop, routes.aiSynonym.view());
    window.open(redirectUrl, '_blank');
  };

  componentWillReceiveProps(nextProps) {
    const shop = getShop();
    const { steps, sendFeedbackGuideSuccess } = nextProps;
    const { isClickedGuideAtLeastOneTime } = this.state;
    const passedSteps = this.checkStepPassed(steps);
    if (
      passedSteps.includes('V2_FILTER4_ADVANCED') ||
      passedSteps.includes('V2_FILTER5_ADVANCED')
    ) {
      this.setState({ isExploreFilterAdvanced: true });
    }

    if (!isClickedGuideAtLeastOneTime) {
      const allGuideVisibleByFeature = getAllGuideVisibleByFeature(steps);
      let guideOpenFirstCheck = findGuideOpening(passedSteps, allGuideVisibleByFeature, shop);
      this.setState({
        guideOpen: guideOpenFirstCheck
      });
    }

    if (!this.props.sendFeedbackGuideSuccess && sendFeedbackGuideSuccess) {
      this.closeThisForever(this.state.featureClickedClose);
    }
  }

  onSendFeedback = ({ comment, status, guideName }) => {
    const { sendFeedback } = this.props;
    sendFeedback(
      {
        guideName,
        status,
        comment
      },
      guideName
    );
  };

  onShowGuide = () => {
    this.setState({ isCollapseGuide: false }, () => {
      this.updateLocalStorageConfig();
    });
  };

  onCloseGuide = () => {
    this.setState({ isHideGuideForever: true }, () => {
      this.updateLocalStorageConfig();
    });
  };

  closeThisForever = feature => {
    this.setState(
      {
        [`isHideForeverGuide${feature}`]: true
      },
      () => {
        this.updateLocalStorageConfig();
      }
    );
  };

  setFeatureOpen = guideOpen => {
    this.closeThisForever(this.state.featureClickedClose);
    this.setState({
      guideOpen,
      isClickedGuideAtLeastOneTime: true,
      isTriggerStepChildFilter: null,
      isTriggerStepChildFilterAdvanced: null,
      isTriggerStepChildSearch: null,
      isTriggerStepChildRecommend: null,
      isTriggerStepChildPreview: null
    });
  };

  setFeatureClickedClose = feature => {
    this.setState({
      featureClickedClose: feature
    });
    try {
      localStorage.setItem(`${getShop()}-guide-feature-close-clicked`, feature);
    } catch (error) {}
  };

  calculatePercent = (passedSteps = [], allStepVisibleByFeature = []) => {
    const passedStepProcessed = _.groupBy(
      _.map(passedSteps, value => _.slice(value, 0, -1).join(''))
    );
    const numberOfGroupsPassed = Object.keys(passedStepProcessed).filter(featureKey => {
      const feature = DATA_GUIDE_V2.find(feature => feature.keyGuide === featureKey);
      if (feature && passedStepProcessed[featureKey].length === feature?.children?.length) {
        return true;
      }
      return false;
    }).length;
    const numberOfGroupyAll = _.size(
      _.groupBy(_.map(allStepVisibleByFeature, value => _.slice(value, 0, -1).join('')))
    );
    return Math.round((numberOfGroupsPassed / numberOfGroupyAll) * 100);
  };

  render() {
    const {
      i18n,
      steps,
      themes,
      sendingFeedbackFilter,
      sendingFeedbackSearch,
      sendingFeedbackRecommendation,
      loading,
      tenantApp
    } = this.props;
    const {
      indexOpenFilter,
      indexOpenSearch,
      indexOpenRecommend,
      indexOpenPreview,
      indexOpenFilterAdvanced,
      isExploreFilterAdvanced,
      isCollapseGuide,
      isCollapsingGuide,
      isHideGuideForever,
      guideOpen
    } = this.state;
    const passedSteps = this.checkStepPassed(steps);
    const isNewUser = enableShopifyIntegration(tenantApp);

    const allGuideVisibleByFeature = getAllGuideVisibleByFeature(steps);
    const allStepVisibleByFeature = getAllStepVisibleByFeature(allGuideVisibleByFeature);

    const percent = this.calculatePercent(passedSteps, allStepVisibleByFeature);
    const statusInstalledApp = this.checkThemes(themes);

    if (isHideGuideForever) return <></>;

    if (loading)
      return (
        <Card sectioned>
          <SkeletonBodyText />
        </Card>
      );

    return (
      <div className="BC-QuickStartGuideV2__DashboardV3">
        <Card sectioned>
          <div className="heading">
            <Heading>{i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.title')}</Heading>
            {isCollapseGuide && (
              <GuideMenu
                i18n={i18n}
                onShowGuide={this.onShowGuide}
                onCloseGuide={this.onCloseGuide}
              />
            )}
          </div>
          <div className="description-progress-percent">
            <div className="description">
              <span>{i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.description')}</span>
            </div>
            <div className="progress">
              <ProgressBar color={false ? 'critical' : 'primary'} progress={percent} size="small" />
            </div>
            <div className="percent">
              {i18n.translate('AdminHTML.DashboardV3.QuickStartGuide.percentInfo', {
                percent: <TextStyle variation="strong">{percent}%</TextStyle>
              })}
            </div>
          </div>

          {!isCollapseGuide && (
            <>
              <div className={`GuideV2List ${isCollapsingGuide ? 'Collapsing' : ''}`}>
                {allGuideVisibleByFeature.map((item, index) => {
                  const { icon, title, children, keyGuide, feature, childrenAdvanced = [] } = item;
                  const countPassed = children.filter(child =>
                    passedSteps.includes(child.keyStep)
                  ).length;

                  if (this.state[`isHideForeverGuide${feature}`]) return <></>;

                  return (
                    <GuideV2Item
                      i18n={i18n}
                      icon={icon}
                      title={i18n.translate(title)}
                      key={index}
                      isOpen={guideOpen === keyGuide}
                      setFeatureOpen={this.setFeatureOpen}
                      setFeatureClickedClose={this.setFeatureClickedClose}
                      keyGuide={keyGuide}
                      feature={feature}
                      countPassed={countPassed}
                      totalStep={children.length}
                      onSendFeedback={this.onSendFeedback}
                      loading={{
                        sendingFeedbackFilter,
                        sendingFeedbackSearch,
                        sendingFeedbackRecommendation
                      }}
                      closeThisForever={this.closeThisForever}>
                      {children.map((child, indexChild) => {
                        const {
                          title: titleChild,
                          description,
                          btnTexts,
                          img,
                          keyStep,
                          cases
                        } = child;
                        let isOpen = false;
                        if (keyGuide === 'V2_FILTER') {
                          isOpen = indexOpenFilter === indexChild;
                        }
                        if (keyGuide === 'V2_SEARCH') {
                          isOpen = indexOpenSearch === indexChild;
                        }
                        if (keyGuide === 'V2_RECOMMEND') {
                          isOpen = indexOpenRecommend === indexChild;
                        }
                        if (keyGuide === 'V2_PREVIEW') {
                          isOpen = indexOpenPreview === indexChild;
                        }

                        if (keyGuide === 'V2_PREVIEW') {
                          const data = cases[statusInstalledApp];
                          const { btnTexts: btnTextsCase, img: imgCase } = data;

                          const { titleComponent, descriptionComponent } =
                            generateGuidePreviewContent(
                              i18n,
                              statusInstalledApp,
                              data,
                              this.onGotoThemeSetup
                            );

                          if (isNewUser) {
                            return (
                              <GuideV2ItemStep
                                isHideCheckIcon
                                keyStep={keyStep}
                                keyGuide={keyGuide}
                                isOpen={true}
                                key={indexChild}
                                index={indexChild}
                                isChecked={passedSteps.includes(keyStep)}
                                title={i18n.translate(
                                  'AdminHTML.DashboardV3.QuickStartGuideV2.Preview.Preview1_case_ssr_label'
                                )}>
                                <div className="container__flex">
                                  <div className="left">
                                    <div className="description">
                                      {i18n.translate(
                                        'AdminHTML.DashboardV3.QuickStartGuideV2.Preview.Preview1_case_ssr_description'
                                      )}
                                    </div>
                                    <div className="action">
                                      <Button
                                        primary
                                        onClick={() =>
                                          this.renderActionStep(
                                            keyStep,
                                            STATUS_INSTALL_ON_THEME.SSR
                                          )
                                        }>
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                          }}>
                                          {i18n.translate(
                                            'AdminHTML.DashboardV3.QuickStartGuideV2.Preview.Preview1_case_ssr_btn'
                                          )}
                                        </div>
                                      </Button>
                                    </div>
                                  </div>

                                  <div className="img">
                                    <img
                                      className="icon"
                                      src={ImgPreviewSSR}
                                      alt="stepIconUncheck"
                                      width={285}
                                      height={140}
                                    />
                                  </div>
                                </div>
                              </GuideV2ItemStep>
                            );
                          }

                          return (
                            <GuideV2ItemStep
                              isHideCheckIcon
                              keyStep={keyStep}
                              keyGuide={keyGuide}
                              isOpen={true}
                              key={indexChild}
                              index={indexChild}
                              isChecked={passedSteps.includes(keyStep)}
                              titleComponent={titleComponent}>
                              <div className="container__flex">
                                <div className="left">
                                  <div className="description">{descriptionComponent}</div>
                                  <div className="action">
                                    {btnTextsCase.map((btnTextItem, index) => {
                                      return (
                                        <Button
                                          primary
                                          key={index}
                                          onClick={() =>
                                            this.renderActionStep(keyStep, statusInstalledApp)
                                          }>
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px'
                                            }}>
                                            {i18n.translate(btnTextItem)}
                                          </div>
                                        </Button>
                                      );
                                    })}
                                  </div>
                                </div>

                                <div className="img">
                                  <img
                                    className="icon"
                                    src={imgCase}
                                    alt="stepIconUncheck"
                                    width={285}
                                    height={140}
                                  />
                                </div>
                              </div>
                            </GuideV2ItemStep>
                          );
                        }

                        let firstStatusOpen = null;
                        if (guideOpen === keyGuide) {
                          const stepOpen = steps
                            .filter(step => step.name.includes(keyGuide))
                            .find(step => !step.isDone);
                          firstStatusOpen = stepOpen
                            ? stepOpen.name === keyStep &&
                              this.state[mapFeature[keyGuide]] === null &&
                              !this.state[mapTrigger[keyGuide]]
                            : isOpen;
                        }

                        return (
                          <GuideV2ItemStep
                            firstStatusOpen={firstStatusOpen}
                            keyStep={keyStep}
                            keyGuide={keyGuide}
                            isOpen={isOpen}
                            onOpen={this.onOpen}
                            key={indexChild}
                            index={indexChild}
                            isChecked={passedSteps.includes(keyStep)}
                            title={i18n.translate(titleChild)}>
                            <div className="container__flex">
                              <div className="left">
                                <div className="description">{i18n.translate(description)}</div>
                                <div className="action">
                                  {btnTexts.map((btnTextItem, index) => {
                                    return (
                                      <Button
                                        primary={index === 0}
                                        key={index}
                                        onClick={() => this.renderActionStep(keyStep, index)}>
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                          }}>
                                          {index === 1 && (
                                            <img
                                              className="status"
                                              src={IconReload}
                                              alt="IconReload"
                                              width={20}
                                              height={20}
                                            />
                                          )}
                                          {i18n.translate(btnTextItem)}
                                        </div>
                                      </Button>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="img">
                                <img
                                  className="icon"
                                  src={img}
                                  alt="stepIconUncheck"
                                  width={285}
                                  height={140}
                                />
                              </div>
                            </div>
                          </GuideV2ItemStep>
                        );
                      })}

                      {/* Guide filter done - show advanced - screen 1 */}
                      {keyGuide === 'V2_FILTER' &&
                        countPassed === 3 &&
                        !isExploreFilterAdvanced && (
                          <div className="GuideV2Item__Advanced">
                            <div className="GuideV2Item__Advanced--Screen">
                              <div className="title">
                                <img
                                  className="IconStar"
                                  src={IconStar}
                                  alt="IconStar"
                                  width={20}
                                  height={20}
                                />
                                <span>
                                  {i18n.translate(
                                    'AdminHTML.DashboardV3.QuickStartGuideV2.Filter.advanced_title'
                                  )}
                                </span>
                              </div>
                              <div className="action">
                                <Button
                                  onClick={() => this.setState({ isExploreFilterAdvanced: true })}>
                                  {i18n.translate(
                                    'AdminHTML.DashboardV3.QuickStartGuideV2.Filter.advanced_btn'
                                  )}
                                </Button>
                                <span
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => this.startSupport('filter')}>
                                  <u>
                                    {i18n.translate(
                                      'AdminHTML.DashboardV3.QuickStartGuideV2.Filter.advanced_no_need'
                                    )}
                                  </u>
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                      {/* Guide filter done - show advanced - screen 2 - add 2 step */}
                      {keyGuide === 'V2_FILTER' && countPassed === 3 && isExploreFilterAdvanced && (
                        <>
                          <div className="GuideV2Item__Advanced">
                            <div className="GuideV2Item__Advanced--Screen">
                              <div className="title">
                                <img
                                  className="IconStar"
                                  src={IconStar}
                                  alt="IconStar"
                                  width={20}
                                  height={20}
                                />
                                <span>
                                  {i18n.translate(
                                    'AdminHTML.DashboardV3.QuickStartGuideV2.Filter.advanced_title2'
                                  )}
                                </span>
                              </div>
                              <div className="action"></div>
                            </div>
                          </div>
                          {childrenAdvanced.map((child, indexChild) => {
                            const {
                              title: titleChild,
                              description,
                              btnTexts,
                              img,
                              keyStep
                            } = child;

                            const isOpen = indexOpenFilterAdvanced === indexChild;

                            const stepOpen = steps
                              .filter(
                                step =>
                                  step.name.includes(keyGuide) && step.name.includes('ADVANCED')
                              )
                              .find(step => !step.isDone);
                            const firstStatusOpen = stepOpen
                              ? stepOpen.name === keyStep &&
                                this.state.indexOpenFilterAdvanced === null &&
                                !this.state.isTriggerStepChildFilterAdvanced
                              : isOpen;

                            return (
                              <GuideV2ItemStep
                                firstStatusOpen={firstStatusOpen}
                                keyStep={keyStep}
                                keyGuide={keyGuide}
                                isOpen={isOpen}
                                onOpen={this.onOpenFilterAdvanced}
                                key={indexChild}
                                index={indexChild}
                                isChecked={passedSteps.includes(keyStep)}
                                title={i18n.translate(titleChild)}>
                                <div className="container__flex">
                                  <div className="left">
                                    <div className="description">{i18n.translate(description)}</div>
                                    <div className="action">
                                      {btnTexts.map((btnTextItem, index) => {
                                        return (
                                          <Button
                                            primary={index === 0}
                                            key={index}
                                            onClick={() => this.renderActionStep(keyStep, index)}>
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                              }}>
                                              {index === 1 && (
                                                <img
                                                  className="status"
                                                  src={IconReload}
                                                  alt="IconReload"
                                                  width={20}
                                                  height={20}
                                                />
                                              )}
                                              {i18n.translate(btnTextItem)}
                                            </div>
                                          </Button>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  <div className="img">
                                    <img
                                      className="icon"
                                      src={img}
                                      alt="stepIconUncheck"
                                      width={285}
                                      height={140}
                                    />
                                  </div>
                                </div>
                              </GuideV2ItemStep>
                            );
                          })}
                        </>
                      )}

                      {/* Guide search done - show advanced - screen 1 */}
                      {keyGuide === 'V2_SEARCH' && countPassed === 2 && (
                        <div className="GuideV2Item__Advanced">
                          <div className="GuideV2Item__Advanced--Screen">
                            <div className="title">
                              <img
                                className="IconStar"
                                src={IconStar}
                                alt="IconStar"
                                width={20}
                                height={20}
                              />
                              <span>
                                {i18n.translate(
                                  'AdminHTML.DashboardV3.QuickStartGuideV2.Search.advanced_title'
                                )}
                              </span>
                            </div>
                            <div className="action">
                              <Button onClick={this.onExploreAddon}>
                                {i18n.translate(
                                  'AdminHTML.DashboardV3.QuickStartGuideV2.Search.advanced_btn'
                                )}
                              </Button>
                              <span
                                style={{ cursor: 'pointer' }}
                                onClick={() => this.startSupport('search')}>
                                <u>
                                  {i18n.translate(
                                    'AdminHTML.DashboardV3.QuickStartGuideV2.Search.advanced_no_need'
                                  )}
                                </u>
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </GuideV2Item>
                  );
                })}
              </div>
              {!(isCollapsingGuide || isCollapseGuide) && percent !== 100 && (
                <div className="GuideV2Dismiss">
                  <span
                    onClick={() => {
                      this.setState({ isCollapsingGuide: true });

                      setTimeout(() => {
                        this.setState({ isCollapseGuide: true }, () => {
                          this.updateLocalStorageConfig();
                        });
                        this.setState({ isCollapsingGuide: false });
                      }, 1000);
                    }}>
                    {isNewUser
                      ? i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.all_done')
                      : i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.dismiss_guide')}
                  </span>
                </div>
              )}
              {percent === 100 && (
                <div className="GuideV2Done">
                  <div className="GuideV2Done__text">
                    <img
                      className="IconChecked"
                      src={IconChecked}
                      alt="IconChecked"
                      width={20}
                      height={20}
                    />
                    <span>
                      {i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.congrats')}
                    </span>
                  </div>
                  <Button
                    onClick={() => {
                      this.setState({ isCollapsingGuide: true });
                      setTimeout(() => {
                        this.setState({ isHideGuideForever: true }, () => {
                          this.updateLocalStorageConfig();
                        });
                        this.setState({ isCollapsingGuide: false });
                      }, 1000);
                    }}>
                    {i18n.translate('AdminHTML.DashboardV3.QuickStartGuideV2.btn_close_guide')}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    );
  }

  // Define and render action list by feature
  renderActionStep = (nameFeature, indexBtn) => {
    const { defaultFilterId, markDoneStep } = this.props;
    const shop = getShop();

    const actionByStep = {
      V2_FILTER1: () => history.push(routes.filterTree.view(defaultFilterId)),
      V2_FILTER2: () => history.push(routes.filterSettings.view()),
      V2_FILTER3: () => history.push(routes.filterLayout.view()),
      V2_SEARCH1: () => {
        const redirectUrl = buildRedirectUrl(shop, routes.relevanceSettings.view());
        window.open(redirectUrl, '_blank');
      },
      V2_SEARCH2: () => history.push(routes.merchandising.view()),
      V2_RECOMMEND1: () => {
        const redirectUrl = buildRedirectUrl(shop, routes.recommendationWidgets.list(1));
        window.open(redirectUrl, '_blank');
      },
      V2_RECOMMEND2: () => {
        const redirectUrl = buildRedirectUrl(shop, routes.recommendationWidgets.list());
        window.open(redirectUrl, '_blank');
      },
      V2_PREVIEW1: indexCase => {
        switch (indexCase) {
          case STATUS_INSTALL_ON_THEME.NOT_INSTALLED_ON_THEME:
            history.push(routes.themeSetup.view());
            break;
          case STATUS_INSTALL_ON_THEME.INSTALLED_ON_NOT_LIVE_THEME:
            window.open(getFullAppUrlThemes(), '_blank');
            break;
          case STATUS_INSTALL_ON_THEME.INSTALLED_ON_LIVE_THEME:
            markDoneStep('V2_PREVIEW1', {
              isDone: true
            });
            window.open(getFrontStoreLink(), '_blank');
            break;
          case STATUS_INSTALL_ON_THEME.SSR:
            markDoneStep('V2_PREVIEW1', {
              isDone: true
            });
            history.push(routes.shopifyIntegration.view());
            break;
          default:
            break;
        }
      },
      V2_FILTER4_ADVANCED: () => {
        const redirectUrl = buildRedirectUrl(shop, routes.customSorting.view());
        window.open(redirectUrl, '_blank');
      },
      V2_FILTER5_ADVANCED: () => {
        const redirectUrl = buildRedirectUrl(shop, routes.swatchSettings.view());
        window.open(redirectUrl, '_blank');
      }
    };
    return actionByStep[nameFeature](indexBtn);
  };

  handleSkipGuide = () => {
    this.props.dismissDashboardBlock('quickStartGuide');
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const defaultFilterTrees = _.get(state.managementFilterTree, 'defaultFilterTrees', []);
  const findValueFilterTree = defaultFilterTrees.find(e => e.isDefault);
  const { themeSetupV3 } = state;
  const tenantAppGroups = _.get(state.oauth, 'tenantAppGroups', []);
  let isAdaptMinTotalValue = false;
  for (let group of tenantAppGroups) {
    const totalValueOfPlans = group.reduce((cur, next) => {
      return cur + _.get(next, 'plantDetail.price', 0);
    }, 0);
    if (totalValueOfPlans >= MIN_TOTAL_VALUE_OF_PLANS) {
      isAdaptMinTotalValue = true;
      break;
    }
  }
  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const originalPlan = _.get(chargeInfo, 'name', '');
  const currentTotalProducts = _.get(state.oauth, 'tenantApp.appParams.currentTotalProducts', 0);
  // Get correct plan
  const correctPlan = !_.isEmpty(chargeInfo)
    ? getCorrectPlan(chargeInfo, packagePlans, currentTotalProducts)
    : {};
  const correctPlanPrice = correctPlan?.price;
  return {
    tenantApp,
    defaultFilterId: _.get(findValueFilterTree, 'filterId', ''),
    steps: _.get(state.quickStartGuide, 'steps', []),
    loading: _.get(state.quickStartGuide, 'loading', false),
    success: _.get(state.quickStartGuide, 'success', false),
    step: _.get(state.quickStartGuide, 'step', {}),
    themes: _.get(themeSetupV3, 'themes', []),
    isAdaptMinTotalValue,
    correctPlanPrice,
    originalPlan,
    sendingFeedbackFilter: _.get(state.quickStartGuide, 'sendingFeedbackFilter', false),
    sendingFeedbackSearch: _.get(state.quickStartGuide, 'sendingFeedbackSearch', false),
    sendingFeedbackRecommendation: _.get(
      state.quickStartGuide,
      'sendingFeedbackRecommendation',
      false
    ),
    sendFeedbackFilterSuccess: _.get(state.quickStartGuide, 'sendFeedbackFilterSuccess', false),
    sendFeedbackSearchSuccess: _.get(state.quickStartGuide, 'sendFeedbackSearchSuccess', false),
    sendFeedbackRecommendSuccess: _.get(
      state.quickStartGuide,
      'sendFeedbackRecommendSuccess',
      false
    ),
    sendFeedbackGuideSuccess: _.get(state.quickStartGuide, 'sendFeedbackGuideSuccess', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  listFilterTree: (q, skip, limit) =>
    dispatch(managementActions.listFilterTree(q, skip, limit, ownProps.i18n)),
  getQuickStartGuides: () => dispatch(quickStartGuideAction.getQuickStartGuides(ownProps.i18n)),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, ownProps.i18n)),
  sendFeedback: (data, feature) =>
    dispatch(quickStartGuideAction.sendFeedback(data, feature, ownProps.i18n)),
  dismissDashboardBlock: (blockName: string) =>
    dispatch(dashboardActionV3.processDismissBlock(blockName, ownProps.i18n)),
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(QuickStartGuideV2));
